import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Moon, Compass, ArrowUp } from 'lucide-react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { MoonDayData } from '@/utils/moonCalculations';

interface MoonCalendarTableProps {
  monthData: MoonDayData[];
}

export const MoonCalendarTable: React.FC<MoonCalendarTableProps> = ({ monthData }) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <TooltipProvider>
        <div className="space-y-4">
          {monthData.map((day) => (
            <Card key={day.date} className={`glass-card border-border/50 shadow-md ${
              day.isSupermoon ? 'bg-amber-100/80 border-l-4 border-l-yellow-500' :
              day.isFullMoon ? 'bg-yellow-100/60 border-l-4 border-l-yellow-400' : ''
            }`}>
              <CardContent className="p-4">
                <div className="space-y-3">
                  {/* Date and Phase */}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">{day.fullDate}</h3>
                      {day.isSupermoon && (
                        <Badge className="bg-yellow-500 text-white text-xs">✨ Supermoon</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{day.moonPhaseEmoji}</span>
                      <span className="text-sm text-muted-foreground">{day.illumination}%</span>
                    </div>
                  </div>
                  
                  {/* Phase Name */}
                  <div className="text-sm text-muted-foreground">
                    {day.moonPhaseName}
                  </div>

                  {/* Moonrise with Azimuth */}
                  <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-galaxy-blue mb-1">
                        <Moon className="w-4 h-4" />
                        <span className="text-xs font-medium">Moonrise</span>
                      </div>
                      <p className="text-sm font-mono">{day.moonrise}</p>
                      <div className="flex items-center justify-center gap-1 mt-1">
                        <Compass className="w-3 h-3 text-galaxy-blue" />
                        <span className="text-xs text-galaxy-blue font-medium">{day.moonriseAzimuth}</span>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-galaxy-blue mb-1">
                        <Moon className="w-4 h-4 opacity-50" />
                        <span className="text-xs font-medium">Moonset</span>
                      </div>
                      <p className="text-sm font-mono">{day.moonset}</p>
                      <div className="flex items-center justify-center gap-1 mt-1">
                        <Compass className="w-3 h-3 text-galaxy-blue" />
                        <span className="text-xs text-galaxy-blue font-medium">{day.moonsetAzimuth}</span>
                      </div>
                    </div>
                  </div>

                  {/* Max Altitude */}
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center justify-center gap-2">
                      <ArrowUp className="w-4 h-4 text-gold" />
                      <span className="text-xs font-medium text-gold">Max Altitude</span>
                    </div>
                    <p className="text-center text-sm font-mono mt-1">
                      {day.maxAltitude}° at {day.maxAltitudeTime}
                    </p>
                  </div>

                  {/* Twilight Note */}
                  {day.twilightNote && (
                    <div className="text-center p-2 bg-amber-50 rounded-lg">
                      <span className="text-xs text-amber-700">🌅 {day.twilightNote}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Card className="glass-card border-border/50 shadow-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 bg-secondary/20">
                  <TableHead className="text-foreground font-medium py-4 min-w-[100px] sticky left-0 bg-secondary/20 z-10">Date</TableHead>
                  <TableHead className="text-muted-foreground font-medium min-w-[50px] text-center">Phase</TableHead>
                  <TableHead className="text-galaxy-blue font-medium min-w-[80px]">
                    <div className="flex items-center">
                      <Moon className="w-3 h-3 mr-1" />
                      Rise
                    </div>
                  </TableHead>
                  <TableHead className="text-galaxy-blue font-medium min-w-[80px]">
                    <div className="flex items-center">
                      <Compass className="w-3 h-3 mr-1" />
                      Rise Dir
                    </div>
                  </TableHead>
                  <TableHead className="text-galaxy-blue font-medium min-w-[80px]">
                    <div className="flex items-center">
                      <Moon className="w-3 h-3 mr-1 opacity-50" />
                      Set
                    </div>
                  </TableHead>
                  <TableHead className="text-galaxy-blue font-medium min-w-[80px]">
                    <div className="flex items-center">
                      <Compass className="w-3 h-3 mr-1" />
                      Set Dir
                    </div>
                  </TableHead>
                  <TableHead className="text-gold font-medium min-w-[100px] hidden lg:table-cell">
                    <div className="flex items-center">
                      <ArrowUp className="w-3 h-3 mr-1" />
                      Max Alt
                    </div>
                  </TableHead>
                  <TableHead className="text-muted-foreground font-medium min-w-[50px] text-center hidden sm:table-cell">%</TableHead>
                  <TableHead className="text-amber-600 font-medium min-w-[120px] hidden xl:table-cell">Twilight</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthData.map((day) => (
                  <TableRow 
                    key={day.date} 
                    className={`border-border/30 hover:bg-secondary/20 transition-colors ${
                      day.isSupermoon ? 'bg-amber-100/80' :
                      day.isFullMoon ? 'bg-yellow-100/60' : ''
                    }`}
                  >
                    <TableCell className={`text-foreground font-medium py-3 sticky left-0 z-10 border-r border-border/30 ${
                      day.isSupermoon ? 'bg-amber-100 border-l-4 border-l-yellow-500' :
                      day.isFullMoon ? 'bg-yellow-100 border-l-4 border-l-yellow-400' : 'bg-background'
                    }`}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{day.fullDate}</span>
                        {day.isSupermoon && (
                          <Badge className="bg-yellow-500 text-white text-xs">✨</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xl text-center">
                      {day.moonPhaseEmoji}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">{day.moonrise}</TableCell>
                    <TableCell className="text-galaxy-blue text-xs font-medium">{day.moonriseAzimuth}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{day.moonset}</TableCell>
                    <TableCell className="text-galaxy-blue text-xs font-medium">{day.moonsetAzimuth}</TableCell>
                    <TableCell className="text-gold text-xs hidden lg:table-cell">
                      {day.maxAltitude}° at {day.maxAltitudeTime}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-center text-xs hidden sm:table-cell">
                      {day.illumination}%
                    </TableCell>
                    <TableCell className="text-amber-600 text-xs hidden xl:table-cell">
                      {day.twilightNote || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
